import React, { createContext, useContext, useEffect, useState } from 'react';
import { buildSiteSettingsState } from '../lib/siteDefaults';
import { fetchJsonCached } from '../lib/settingsCache';
import { setSeoBrand } from '../lib/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SiteSettingsContext = createContext(buildSiteSettingsState({}, true));

export const useSiteSettings = () => useContext(SiteSettingsContext);

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => buildSiteSettingsState({}, true));

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const fetchedSettings = await fetchJsonCached(`${API_URL}/api/settings/site`);
        
        // Build full URLs for relative paths
        if (fetchedSettings.logo_url && fetchedSettings.logo_url.startsWith('/')) {
          fetchedSettings.logo_url = `${API_URL}${fetchedSettings.logo_url}`;
        }
        
        const nextSettings = buildSiteSettingsState(fetchedSettings, false);

        // feed SEO helpers the real brand so titles/og tags are never hardcoded
        setSeoBrand({ siteName: nextSettings.siteName, siteUrl: nextSettings.siteUrl });

        if (nextSettings.siteName) {
          document.title = nextSettings.siteName;
        }

        if (nextSettings.faviconUrl) {
          // Build the full URL if it's a relative path
          let faviconFullUrl = nextSettings.faviconUrl;
          if (faviconFullUrl.startsWith('/')) {
            faviconFullUrl = `${API_URL}${faviconFullUrl}`;
          }
          
          // Update ALL favicon-related link elements
          const faviconSelectors = [
            "link[rel='icon']",
            "link[rel='shortcut icon']",
            "link[rel='apple-touch-icon']"
          ];
          
          faviconSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(el => {
              el.href = faviconFullUrl;
            });
          });
          
          // If no icon exists, create one
          if (!document.querySelector("link[rel='icon']")) {
            const iconLink = document.createElement('link');
            iconLink.type = 'image/x-icon';
            iconLink.rel = 'icon';
            iconLink.href = faviconFullUrl;
            document.head.appendChild(iconLink);
          }
        }

        window.__DEBUG_MODE__ = nextSettings.debugMode;
        if (nextSettings.debugMode) {
          console.log('[DEBUG MODE] Site settings loaded:', fetchedSettings);
        }

        setSettings(nextSettings);
      } catch (error) {
        console.error('Error fetching site settings:', error);
        window.__DEBUG_MODE__ = false;
        setSettings(buildSiteSettingsState({}, false));
      }
    };

    fetchSiteSettings();
  }, []);

  return (
    <SiteSettingsContext.Provider value={settings}>
      {children}
    </SiteSettingsContext.Provider>
  );
};